// Please refer to: https://ng-alain.com/docs/i18n
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { registerLocaleData } from '@angular/common';
import ngEn from '@angular/common/locales/en';
import ngAr from '@angular/common/locales/ar';
import ngEs from '@angular/common/locales/es';
import ngFr from '@angular/common/locales/fr';
import ngRu from '@angular/common/locales/ru';


import * as df_en from 'date-fns/locale/en-US';
import * as df_ar from 'date-fns/locale/ar-SA';
import * as df_es from 'date-fns/locale/es';
import * as df_ru from 'date-fns/locale/ru';
import * as df_FR from 'date-fns/locale/fr';

import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import {
  SettingsService,
  AlainI18NService
} from '@delon/theme';
import { EnvironmentService } from '../../services/environment.service';
import { StorageService } from '../../services/storage.service';
import * as moment from 'moment';
import { ar_EG, en_US, es_ES, fr_FR, NzI18nService, ru_RU } from 'ng-zorro-antd/i18n';


interface LangData {
  text: string;
  ng: any;
  zorro: any;
  dateFns: any;
  abbr: string;
  resource: string;
  direction: string;
}

const DEFAULT = 'en-US';
const LANGS: { [key: string]: LangData } = {

  'en-US': {
    text: 'English',
    ng: ngEn,
    zorro: en_US,
    dateFns: df_en,
    abbr: 'flag-icon-us',
    resource: 'en',
    direction: 'ltr'
  },
  'ar-EG': {
    text: 'عربي',
    ng: ngAr,
    zorro: ar_EG,
    dateFns: df_ar,
    abbr: 'flag-icon-ae',
    resource: 'ar',
    direction: 'rtl'
  },
  'es-ES': {
    text: 'Española',
    ng: ngEs,
    zorro: es_ES,
    dateFns: df_es,
    abbr: 'flag-icon-es',
    resource: 'es',
    direction: 'ltr'
  },
  'ru-RU': {
    text: 'Русский',
    ng: ngRu,
    zorro: ru_RU,
    dateFns: df_ru,
    abbr: 'flag-icon-ru',
    resource: 'ru',
    direction: 'ltr'
  },
  'fr-FR': {
    text: 'Français',
    ng: ngFr,
    zorro: fr_FR,
    dateFns: df_FR,
    abbr: 'flag-icon-fr',
    resource: 'fr',
    direction: 'ltr'
  },
};

@Injectable({ providedIn: 'root' })
export class I18NService implements AlainI18NService {
  private _default:any = DEFAULT;
  private change$ = new BehaviorSubject<string>('');
  private langs$ = new BehaviorSubject<any[]>([]);

  private _langs = Object.keys(LANGS).map(code => {
    const item = LANGS[code];
    return { code, text: item.text, abbr: item.abbr, resource: item.resource };
  });

  constructor(
    settings: SettingsService,
    private nzI18nService: NzI18nService,
    private translate: TranslateService,
    private envService: EnvironmentService,
    private storageService: StorageService
  ) {
    this._default = this.storageService.getSessionStorageItem('defaultLang') || DEFAULT;
    const defaultLan:any = settings.layout.lang || translate.getBrowserLang();
    // `@ngx-translate/core` knows which languages are supported in advance
    const lans = this._langs.map(item => {
      return item.code;
    });
    translate.addLangs(lans);
    this._default = lans.indexOf(defaultLan) > 0 ? defaultLan : lans[0];
    this.updateLangData(this._default);
    this.change$.subscribe(res => {
      console.log('change$ -> ', res);
    });
  }

  private updateLangData(lang: string) {
    const item = LANGS[lang];
    registerLocaleData(item.ng);
    document.body.setAttribute('dir', item.direction);
    this.nzI18nService.setLocale(item.zorro);
    moment.locale(item.resource);
    (window as any).__locale__ = item.dateFns;
  }

  get change(): Observable<string> {
    return this.change$.asObservable().pipe(filter(w => {
      return w != null;
    }));
  }

  use(lang: string): void {
    lang = lang || this.translate.getDefaultLang();
    if (this.currentLang === lang) return;
    this.updateLangData(lang);
    this.storageService.setSessionStorageItem('defaultLang', lang);
    this.translate.use(lang).subscribe(() => {
      return this.change$.next(lang);
    });
  }
  /** Get the list of languages */
  getLangs() {
    return this._langs;
  }

  setLanguages() {
    let configLangs:any[] = [];
    if (this.envService.getEnvironment().languages == '')
      configLangs = ['en'];
    else
      configLangs = this.envService.getEnvironment().languages.split(',');
    const langs:any[] = [];
    this._langs.map(lang => {
      if (configLangs.indexOf(lang.resource) >= 0)
        langs.push(lang);
    });
    this.langs$.next(langs);
  }
  getLanguages() {
    return this.langs$;
  }
  /** Translation */
  fanyi(key: string, interpolateParams?: object) {
    return this.translate.instant(key, interpolateParams);
  }
  /** default language */
  get defaultLang() {
    return this._default;
  }

  getLangResource(lang?: string) {
    return LANGS[_.isEmpty(lang) ? this._default : lang].resource;
  }
  /** Current language */
  get currentLang() {
    return (
      this.translate.currentLang ||
      this.translate.getDefaultLang() ||
      this._default
    );
  }
}
